import crmApi from '../../utils/API';

// Client for the sourcing API (cv-scanner-ia/sourcing/backend).
// Local CRM -> local API (localhost:8000); deployed CRM -> /sourcing on the same domain.
// REACT_APP_SOURCING_API_URL overrides it when set.
// LinkedIn pages are collected in this browser by the Sourcing extension when installed
// (see extensionBridge.js), otherwise by the server with its own session (see LinkedinLoginPage).
const LOCAL_HOSTS = ['localhost', '127.0.0.1', '[::1]'];

const resolveApiUrl = () => {
  if (process.env.REACT_APP_SOURCING_API_URL) return process.env.REACT_APP_SOURCING_API_URL;
  if (LOCAL_HOSTS.includes(window.location.hostname)) return 'http://localhost:8000';
  return `${window.location.origin}/sourcing`;
};

export const SOURCING_API_URL = resolveApiUrl().replace(/\/$/, '');
// 'browser' = collected by the Sourcing extension in this browser; 'playwright' = server session.
let collectorMode = 'playwright';
export const setCollectorMode = (mode) => { collectorMode = mode; };

async function request(path, { method = 'GET', body, form, fallback = 'Service sourcing indisponible' } = {}) {
  let res;
  try {
    res = await fetch(`${SOURCING_API_URL}${path}`, {
      method,
      headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      body: form || (body !== undefined ? JSON.stringify(body) : undefined),
    });
  } catch (e) {
    throw new Error('Le service de sourcing est injoignable.');
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const detail = typeof data.detail === 'string' ? data.detail : null;
    throw new Error(detail || fallback);
  }
  return res.json().catch(() => ({}));
}

const qs = (params) => new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== null)).toString();

/** Creates (or updates, same LinkedIn) the candidate in this CRM, through the CRM's own API. */
export async function addToCrm(form) {
  const { data } = await crmApi.post('/extension/add', {
    candidatOrContact: 'candidat',
    linkedin: form.linkedin,
    prenom: form.prenom,
    nom: form.nom,
    fonction: form.fonction,
    employeur: form.employeur,
    ecole: form.ecole,
    localisation: form.localisation,
    email: form.email,
    telephone: form.telephone,
    competences: form.competences,
    sachezQue: form.sachez_que || undefined,
    // The CRM parses this as a date: a bare year ("2026") would be read as a time of day.
    dateDebut: /^\d{4}$/.test(String(form.date_debut || '').trim()) ? `${String(form.date_debut).trim()}-01-01` : form.date_debut || undefined,
    viviers: [],
  });
  const id = data?.candidat_id || data?.id;
  if (!id) throw new Error(data?.message || "Le CRM n'a pas renvoyé l'identifiant du candidat.");
  return { id, updated: data?.etat === 'updated' };
}

export const fetchDashboard = () => request('/api/dashboard');
export const fetchLinkedinSession = () => request('/api/linkedin/session');
export const fetchLinkedinAccount = () => request('/api/linkedin/account');
export const disconnectLinkedin = () => request('/api/linkedin/session', { method: 'DELETE' });

// Remote LinkedIn login: the server opens LinkedIn's login page, the CRM shows it and forwards input.
export const startLinkedinLogin = () => request('/api/linkedin/login', { method: 'POST', fallback: "Impossible d'ouvrir LinkedIn" });
const loginPath = (id, action = '') => `/api/linkedin/login/${id}${action ? `/${action}` : ''}`;
export const linkedinLoginState = (id) => request(loginPath(id, 'state'));
export const linkedinLoginClick = (id, x, y) => request(loginPath(id, 'click'), { method: 'POST', body: { x, y } });
export const linkedinLoginType = (id, text) => request(loginPath(id, 'type'), { method: 'POST', body: { text } });
export const linkedinLoginKey = (id, key) => request(loginPath(id, 'key'), { method: 'POST', body: { key } });
export const finishLinkedinLogin = (id) => request(loginPath(id, 'finish'), { method: 'POST', fallback: 'Connexion LinkedIn non terminée' });
export const cancelLinkedinLogin = (id) => request(loginPath(id), { method: 'DELETE' }).catch(() => {});

export async function fetchLinkedinLoginScreen(id) {
  const res = await fetch(`${SOURCING_API_URL}${loginPath(id, 'screen')}`, { cache: 'no-store' });
  if (res.status === 404) throw new Error('expired');
  if (!res.ok) return null;
  return res.blob();
}

export const LINKEDIN_CHANGED_EVENT = 'sourcing:linkedin-changed';
export const notifyLinkedinChanged = () => window.dispatchEvent(new Event(LINKEDIN_CHANGED_EVENT));


/** LinkedIn refused the server session (login wall, security check, no session file). */
export const isLinkedinBlocked = (message) =>
  /authwall|checkpoint|\/login|uas\/login|blocage|session linkedin absente/i.test(String(message || ''));

export const fetchCandidates = (status, limit = 100, offset = 0) =>
  request(`/api/candidates?${qs({ status, limit, offset })}`);

export async function fetchAllCandidates(status) {
  const pageSize = 500;
  const first = await fetchCandidates(status, pageSize, 0);
  const items = [...(first.items || [])];
  const total = Number(first.total ?? items.length);
  for (let offset = items.length; offset < total; offset += pageSize) {
    const next = await fetchCandidates(status, pageSize, offset);
    if (!next.items?.length) break;
    items.push(...next.items);
  }
  return [...new Map(items.map((item) => [item.candidate_uuid || item.linkedin_url, item])).values()];
}

export const fetchSourcedJobs = (limit = 500, offset = 0) => request(`/api/sourced-jobs?${qs({ limit, offset })}`);
export const fetchMissionOperations = (status, limit = 500) => request(`/api/missions/operations?${qs({ status, limit })}`);

export function uploadCsv(file, mode, maxItems = 0) {
  const form = new FormData();
  form.append('file', file);
  form.append('max_items', String(maxItems));
  form.append('collector', collectorMode);
  return request(mode === 'jobs_import' ? '/api/imports/missions' : '/api/imports/candidates', {
    method: 'POST',
    form,
    fallback: "Échec de l'import",
  });
}

export const fetchResults = (jobId) => request(`/api/jobs/${jobId}/results`, { fallback: 'Résultats indisponibles' });

/** Server-sent events of an operation; returns an unsubscribe function. */
export function subscribeEvents(jobId, onEvent) {
  const es = new EventSource(`${SOURCING_API_URL}/api/jobs/${jobId}/events`);
  es.onmessage = (e) => {
    const event = JSON.parse(e.data);
    onEvent(event);
    if (event.type === 'close') es.close();
  };
  es.onerror = () => es.close();
  return () => es.close();
}

const post = (path, body, fallback) => request(path, { method: 'POST', body, fallback });

export const retryStorage = (urls) => post('/api/candidates/retry-storage', { linkedin_urls: urls }, 'La relance du stockage a échoué');
export const retryScraping = (urls) => post('/api/candidates/retry-scraping', { linkedin_urls: urls, collector: collectorMode }, 'La relance du scraping a échoué');
export const retrySmart = (urls) => post('/api/candidates/retry-smart', { linkedin_urls: urls }, 'La correction ciblée a échoué');

export const retryMissionSmart = (urls) => post('/api/missions/retry-smart', { linkedin_urls: urls }, 'Relance impossible');

export const deleteCandidate = (uuid, reason) =>
  request(`/api/candidates/${encodeURIComponent(uuid)}`, { method: 'DELETE', body: { reason }, fallback: 'La suppression du candidat a échoué' });
export const deleteMission = (uuid, reason) =>
  request(`/api/missions/${encodeURIComponent(uuid)}`, { method: 'DELETE', body: { reason }, fallback: 'La suppression de la mission a échoué' });
export const deleteMissionOperation = (linkedinUrl, reason) =>
  request('/api/missions/operations', { method: 'DELETE', body: { linkedin_url: linkedinUrl, reason }, fallback: 'La suppression de la mission a échoué' });

export const runMatching = (direction, sourceId, mode, limit, targetScope) =>
  post(`/api/matching/${direction}`, { source_id: sourceId, mode, limit, target_scope: targetScope }, 'Matching indisponible');
export const promoteMatch = (resource, matchId) =>
  post(`/api/promotions/${resource}/from-match`, { match_id: matchId }, 'Promotion impossible');
